/**
 * The open panel below the canvas, when it does not fit beside it (page-layout.ts): closed in the band the canvas
 * leaves free, half the screen, or all of it but a strip of canvas. The handle is a button (a tap moves on, a
 * keyboard and a screen reader can use it) and a grip (a drag snaps to the nearest state).
 *
 * Its body is a size container named `panel`, as the side panel is: a panel adapts to the room it gets with
 * `@container panel (…)` in its own CSS, never by asking where it is.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Icon } from '../ui-kit/Icon';
import { nearestSheet, SHEET_STATES, type SheetState, type Viewport } from './page-layout';
import css from './BottomSheet.module.css';

const NEXT: Record<SheetState, SheetState> = { peek: 'half', half: 'full', full: 'peek' };
const LABEL: Record<SheetState, string> = { peek: 'Open the panel', half: 'Open the panel fully', full: 'Close the panel' };
const DRAG_SLOP = 6; // px a finger may move and still be a tap

export function BottomSheet({ state, height, viewport, onState, label, children }: {
  state: SheetState;
  height: number;
  viewport: Viewport;
  onState: (state: SheetState) => void;
  label: string;
  children: ReactNode;
}) {
  const drag = useRef<{ startY: number; startHeight: number; moved: boolean } | null>(null);
  const [dragged, setDragged] = useState<number | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { startY: e.clientY, startHeight: height, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dy = d.startY - e.clientY;
    if (Math.abs(dy) > DRAG_SLOP) d.moved = true;
    if (d.moved) setDragged(Math.min(viewport.height, Math.max(0, d.startHeight + dy)));
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.moved && dragged !== null) onState(nearestSheet(viewport, dragged));
    setDragged(null);
  };
  const onClick = () => {
    if (drag.current?.moved) return;
    onState(NEXT[state]);
  };

  return (
    <section
      className={css.sheet}
      style={{ height: dragged ?? height }}
      data-sheet={state}
      data-dragging={dragged !== null || undefined}
      aria-label={label}
    >
      <button
        className={css.handle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onClick}
        aria-label={LABEL[state]}
        aria-expanded={state !== 'peek'}
        title={`${label}: ${SHEET_STATES.indexOf(state) + 1}/${SHEET_STATES.length}`}
      >
        <span className={css.grip} />
        <Icon name={state === 'full' ? 'chevron-down' : 'chevron-up'} size={18} className={css.chevron} />
      </button>
      <div className={css.body}>{children}</div>
    </section>
  );
}
